import { ApiError } from '../identity/types';

/**
 * Payroll History Import — client types. Verbatim from docs/payroll-history-import/api-contract.md
 * (EPN.PayrollHistory.Api). Imported payroll is READ-ONLY informational history: nothing in this feature
 * can pay, deposit, file, post to accounting, approve, reopen, edit or recalculate.
 */
export type BatchStatus =
  | 'Created' | 'Uploaded' | 'Validating' | 'ColumnMappingRequired' | 'ValidationFailed' | 'ReadyForReview'
  | 'Importing' | 'Completed' | 'CompletedWithWarnings' | 'Failed' | 'Canceled';
export type ScanStatus = 'NotStarted' | 'Passed' | 'Failed' | 'NotPerformed';
export type Severity = 'Information' | 'Warning' | 'BlockingError';

/** How well EPN understands a provider's files. The server owns the catalog; only formats EPN owns and tests are
 * ever "NativeTemplate". Every other provider is imported through the guided column mapping. */
export type ProviderSupportLevel = 'NativeTemplate' | 'GuidedMapping' | 'NotVerified';

export const SUPPORT_LABEL = {
  native: 'Native template supported',
  guided: 'Import using guided mapping',
  notVerified: 'Format not yet verified',
} as const;

export interface ProviderInfo {
  id: string;
  displayName: string;
  supportLevel: ProviderSupportLevel;
  /** One of the SUPPORT_LABEL strings, exactly as the server words it. */
  supportLabel: string;
  /** The value stored on a batch (for guided providers "GenericMapped:<provider>"). */
  sourceSystem: string;
  /** Lower-case extensions including the dot, e.g. ".xlsx". */
  acceptedFileTypes: string[];
  description: string;
  /** The template download that belongs to this provider, or null when it has none. */
  templateKind: string | null;
}

export interface Capabilities {
  enabled: boolean;
  canRead: boolean;
  canCreate: boolean;
  canCommit: boolean;
  canAdmin: boolean;
  maxUploadBytes: number;
  sourceSystems: { id: string; displayName: string }[];
  providers: ProviderInfo[];
}

export interface BatchSummary {
  id: string;
  fileName: string | null;
  sourceSystem: string;
  schemaVersion: string | null;
  status: BatchStatus;
  scanStatus: ScanStatus;
  scanDetail: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  importedAtUtc: string | null;
  createdByUserId: string;
  createdByEmail: string;
  payDateStart: string | null;
  payDateEnd: string | null;
  payrollRunCount: number;
  employeeCount: number;
  warningCount: number;
  errorCount: number;
  /** Active (non-voided) runs only; 0 until validated. */
  totalGross: number;
  totalNet: number;
  fileSizeBytes: number | null;
  fileHash: string | null;
  failureReason: string | null;
  concurrencyVersion: number;
  /** The provider the person picked ("EscochexPayroll", "EpnStandardTemplate", "QuickBooks", ...). */
  sourceProvider: string | null;
  /** "Zip", "Workbook" or "Csv". */
  fileFormat: string | null;
}

export interface BatchDetail extends BatchSummary {
  runsImported: number | null;
  runsSkippedAsDuplicate: number | null;
  statementsImported: number | null;
  statementsSkippedAsDuplicate: number | null;
}

export interface BatchPage {
  items: BatchSummary[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface Issue {
  severity: Severity;
  code: string;
  message: string;
  file: string | null;
  rowNumber: number | null;
  sourceRecordId: string | null;
}

export type MappingMethod = 'StoredMapping' | 'ExactExternalId' | 'UserConfirmed' | 'TemplateEmployeeId';

export interface EmployeeMappingRow {
  sourceEmployeeId: string;
  employeeNumber: string | null;
  displayName: string;
  email: string | null;
  employmentStatus: string;
  requiresMapping: boolean;
  mapping: {
    epnEmployeeId: string;
    epnEmployeeNumber: string;
    epnEmployeeName: string;
    epnEmployeeActive: boolean;
    method: MappingMethod;
  } | null;
  suggestion: {
    epnEmployeeId: string;
    epnEmployeeNumber: string;
    epnEmployeeName: string;
    reason: 'EmailMatch';
  } | null;
}

export interface ValidationSummary {
  batchId: string;
  status: BatchStatus;
  sourceCompanyReference: string | null;
  sourceCompanyName: string | null;
  currency: string | null;
  /** True when the only blocking errors are unmapped employees: map, save, and the batch revalidates. */
  canFixByMapping: boolean;
  payRunsDetected: number;
  payPeriodStart: string | null;
  payPeriodEnd: string | null;
  employeesDetected: number;
  employeesMatched: number;
  employeesRequiringMapping: number;
  earningCodesDetected: string[];
  deductionCodesDetected: string[];
  taxJurisdictionsDetected: string[];
  informationCount: number;
  warningCount: number;
  errorCount: number;
  issues: Issue[];
  issuesTruncated: boolean;
  employees: EmployeeMappingRow[];
}

export interface EmployeeOption {
  epnEmployeeId: string;
  employeeNumber: string;
  displayName: string;
  isActive: boolean;
}

export interface SaveMappingsRequest {
  mappings: { sourceEmployeeId: string; epnEmployeeId: string }[];
}

export interface PreviewRun {
  sourcePayrollRunId: string;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  payrollType: string;
  runStatus: 'Completed' | 'Voided';
  employeeCount: number;
  grossPay: number;
  employeeTaxes: number;
  employeeDeductions: number;
  employerTaxes: number;
  netPay: number;
  voidedStatementCount: number;
  correctedStatementCount: number;
  alreadyImported: boolean;
}

export interface Preview {
  batchId: string;
  sourcePlatform: string;
  fileName: string | null;
  /** sourceCompanyName ?? sourceCompanyReference */
  payrollCompany: string | null;
  runCount: number;
  payDateStart: string | null;
  payDateEnd: string | null;
  employeeCount: number;
  grossPay: number;
  employeeTaxes: number;
  employeeDeductions: number;
  employerTaxes: number;
  netPay: number;
  voidedRunCount: number;
  voidedStatementCount: number;
  correctedStatementCount: number;
  warningCount: number;
  errorCount: number;
  runs: PreviewRun[];
}

export interface Statement {
  sourcePayStatementId: string;
  sourceEmployeeId: string;
  employeeDisplayName: string;
  epnEmployeeId: string | null;
  statementStatus: 'Original' | 'Corrected' | 'Voided';
  correctsSourcePayStatementId: string | null;
  grossPay: number;
  taxableWages: number;
  preTaxDeductions: number;
  postTaxDeductions: number;
  employeeTaxes: number;
  employerTaxes: number;
  reimbursements: number;
  netPay: number;
  paymentMethodDescription: string | null;
  /** Already masked at the source (e.g. "****1234"); displayed exactly as given, never unmasked. */
  paymentReference: string | null;
  earnings: {
    sourceLineId: string; code: string; category: string | null; hours: number | null; rate: number | null;
    amount: number; isTaxable: boolean;
  }[];
  deductions: {
    sourceLineId: string; code: string; classification: 'PreTax' | 'PostTax'; employeeAmount: number; employerAmount: number;
  }[];
  taxes: {
    sourceLineId: string; name: string; jurisdiction: string | null; scope: 'Employee' | 'Employer';
    taxableWageBase: number | null; amount: number;
  }[];
}

export interface PreviewRunDetail {
  run: PreviewRun;
  statements: Statement[];
}

export interface CommitRequest {
  confirmationAccepted: boolean;
  expectedConcurrencyVersion: number;
}

export interface HistoricalRun {
  id: string;
  batchId: string;
  dataOrigin: 'LegacyImport';
  sourceSystem: string;
  sourcePayrollRunId: string;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  payrollType: string;
  runStatus: 'Completed' | 'Voided';
  employeeCount: number;
  grossPay: number;
  employeeTaxes: number;
  employeeDeductions: number;
  employerTaxes: number;
  netPay: number;
  importedAtUtc: string;
  importedByEmail: string;
}

export interface HistoricalRunDetail {
  run: HistoricalRun;
  statements: Statement[];
}

export interface HistoricalRunPage {
  items: HistoricalRun[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ---- Client-only types (not part of the HTTP contract) ----

export type TemplateKind = 'template' | 'sample' | 'instructions' | 'standard-template' | 'standard-example';

export interface DownloadedFile {
  blob: Blob;
  fileName: string;
}

export interface BatchListQuery {
  status?: BatchStatus | '';
  sourceSystem?: string;
  from?: string;
  to?: string;
  importedBy?: string;
  page: number;
  pageSize: number;
}

/** ApiError plus the optional machine-readable `code` from the problem body (e.g. PACKAGE_ALREADY_IMPORTED).
 * A subclass, so every `instanceof ApiError` check elsewhere (friendlyErrorMessage) keeps working. */
export class PayrollHistoryApiError extends ApiError {
  /** True when `message` is the server's own problem `detail` (authored for users). False when the client
   * fabricated it (a "Request to ... failed with 413" text) because the body was not problem JSON, e.g. a proxy 413. */
  public readonly serverMessage: boolean;

  constructor(status: number, message: string, code?: string, serverMessage = true) {
    // `code` lives on the base ApiError since EPN-105; `messageIsServerAuthored` is the same fact as `serverMessage`.
    super(status, message, null, serverMessage, code ?? null);
    this.serverMessage = serverMessage;
  }
}

/** What a caller without `read` (HTTP 403 on /capabilities) is treated as: no access to anything. */
export const NO_CAPABILITIES: Capabilities = {
  enabled: false, canRead: false, canCreate: false, canCommit: false, canAdmin: false,
  maxUploadBytes: 0, sourceSystems: [], providers: [],
};

// ---- Guided column mapping (GET /{id}/structure, PUT /{id}/column-mapping, GET /mapping-profiles) ----

/** What a column's values look like. Detected from the file; the values themselves are never sent to the browser. */
export type DetectedType = 'Number' | 'Date' | 'Text' | 'YesNo' | 'Empty';

export interface TabularColumn {
  index: number;
  header: string;
  detectedType: DetectedType | string;
}

export interface TabularSheet {
  name: string;
  columns: TabularColumn[];
  rowCount: number;
}

/** One thing a column can mean. The server owns this list; the screen builds its dropdowns from it. */
export interface MappingTarget {
  role: string;
  label: string;
  help: string;
  group: string;
  singleUse: boolean;
  needsTaxDetail: boolean;
  needsEarningLink: boolean;
}

/** One confirmed decision: "this source column is this kind of thing". Optional members may arrive as null. */
export interface ColumnAssignment {
  sourceColumn: string;
  role: string;
  code?: string | null;
  label?: string | null;
  jurisdictionLevel?: string | null;
  stateCode?: string | null;
  rateType?: string | null;
  taxable?: boolean | null;
}

export interface ColumnMappingConfig {
  version: number;
  providerKey: string;
  sheetName?: string | null;
  columns: ColumnAssignment[];
  sourceCompanyName?: string | null;
  sourceCompanyReference?: string | null;
}

/** EPN's guess for a column, from its header only. Never applied until the person confirms the mapping. */
export interface MappingSuggestion {
  sourceColumn: string;
  role: string;
  reason: string;
  confidence: string;
  jurisdictionLevel?: string | null;
  stateCode?: string | null;
  rateType?: string | null;
  taxable?: boolean | null;
}

export interface SavedMappingProfile {
  id: string;
  name: string;
  updatedAtUtc: string;
  useCount: number;
  mapping: ColumnMappingConfig;
}

export interface Structure {
  batchId: string;
  status: BatchStatus;
  provider: string;
  providerName: string;
  supportLabel: string;
  fileFormat: string;
  sheets: TabularSheet[];
  selectedSheet: string;
  fingerprint: string;
  mappingVersion: number;
  targets: MappingTarget[];
  suggestions: MappingSuggestion[];
  currentMapping: ColumnMappingConfig | null;
  savedProfile: SavedMappingProfile | null;
  issues: Issue[];
}

export interface SaveColumnMappingRequest {
  mapping: ColumnMappingConfig;
  saveProfile: boolean;
  profileName?: string | null;
}

/** The role that is only ever a suggestion: whether a deduction is before or after tax is never guessed. */
export const DEDUCTION_NEEDS_CLASSIFICATION = 'DeductionNeedsClassification';
